import { miraRouteIds, type MiraRouteId } from '@mira/clients-shared';

export interface WebRouteState {
  routeId: MiraRouteId;
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
  sourceRouteId?: MiraRouteId;
}

export function readRouteState(location: Location = window.location): WebRouteState {
  const hashPath = location.hash.startsWith('#') ? location.hash.slice(1) : '';
  const raw = hashPath || location.pathname || '/';
  const url = new URL(raw, location.origin);
  const path = normalizePath(url.pathname);
  const source = url.searchParams.get('source');
  const route = matchRoute(path);
  return {
    ...route,
    path,
    query: url.searchParams,
    sourceRouteId: sourceRouteId(source),
  };
}

function sourceRouteId(source: string | null): MiraRouteId | undefined {
  if (!source) return undefined;
  if (miraRouteIds.includes(source as MiraRouteId)) return source as MiraRouteId;
  return matchRoute(normalizePath(source)).routeId;
}

function normalizePath(path: string): string {
  if (!path || path === '/') return '/';
  return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
}

function matchRoute(path: string): Pick<WebRouteState, 'routeId' | 'params'> {
  if (path === '/') return route('startup');
  if (path === '/auth') return route('login');
  if (path === '/register') return route('register');
  if (path === '/onboarding/profile') return route('onboarding-profile');
  if (path === '/onboarding/birth') return route('onboarding-birth');
  if (path === '/onboarding/relationship') return route('onboarding-relationship');
  if (path === '/today') return route('today');
  if (path === '/topics') return route('topics');
  if (path.startsWith('/topics/')) return route('topic-detail', { topicId: path.slice('/topics/'.length) });
  if (path === '/relationships') return route('relationships');
  if (path === '/relationships/new') return route('relationship-new');
  if (path.startsWith('/relationships/')) return route('relationship-detail', { relationshipId: path.slice('/relationships/'.length) });
  if (path === '/memory') return route('memory');
  if (path === '/notes' || path.startsWith('/notes/')) return route('memory');
  if (path === '/settings') return route('settings');
  if (path === '/settings/profile') return route('settings-profile');
  if (path === '/settings/birth') return route('settings-birth');
  if (path === '/settings/password') return route('settings-password');
  if (path === '/settings/privacy') return route('settings-privacy');
  if (path === '/privacy') return route('privacy');
  if (path === '/terms') return route('terms');
  if (path.startsWith('/chat/')) return route('chat', { conversationId: path.slice('/chat/'.length) });
  return route('startup');
}

function route(routeId: MiraRouteId, params: Record<string, string> = {}) {
  return { routeId, params };
}
